'use client'
import type { FormFieldBlock, Form as FormType } from '@payloadcms/plugin-form-builder/types'

import { useRouter } from 'next/navigation'
import React, { useCallback, useMemo, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { Info, LoaderCircle } from 'lucide-react'
import RichText from '@/components/RichText'
import { Button } from '@/components/ui/button'
import { ThankYouMark } from '@/components/forms/ThankYouMark'
import { cn } from '@/utilities/ui'
import type { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'

import { fields } from './fields'
import { getClientSideURL } from '@/utilities/getURL'
import { extractContactFromFormData, formDataToFields, reportToLeadCentre } from '@/utilities/leadCentre'
import type { LeadCentreLeadType } from '@/utilities/leadCentre'

/**
 * Property Alerts carries an `alert-preferences` checkbox group (Residential/
 * Commercial × Sales/Rentals — CheckboxGroup/index.tsx stores the selection as a
 * comma-joined string, e.g. "residential-sales, commercial-rentals"). A
 * selection naming only rentals or only sales gives a real buy/rent signal;
 * a mixed or empty selection doesn't, so it falls back to OTHER rather than
 * guessed at.
 */
function leadCentreTypeForPropertyAlerts(
  dataToSend: { field: string; value: unknown }[],
): LeadCentreLeadType {
  const prefs = String(
    dataToSend.find((d) => d.field === 'alert-preferences')?.value || '',
  ).toLowerCase()
  const wantsRental = prefs.includes('rentals')
  const wantsSale = prefs.includes('sales')
  if (wantsRental && !wantsSale) return 'RENTAL_ENQUIRY'
  if (wantsSale && !wantsRental) return 'BUYER_ENQUIRY'
  return 'OTHER'
}

/**
 * Lead Centre mapping for forms rendered through this generic Form Builder block.
 * Refer A Client and Careers EOI carry no signal in their fields for buy vs.
 * sell vs. rent, so both report as OTHER rather than guessed at (Asana
 * lead-centre-wiring brief, 2026-08-24). Form id '5' is the legacy appraisal
 * step-1 form; no page currently renders it (the appraisal flow now runs
 * through the dedicated /appraisal wizard, wired separately in AppraisalStep4),
 * but the mapping is kept per the brief in case it's reinstated.
 */
function leadCentreTypeForForm(
  formID: string | number | undefined,
  title: string,
  dataToSend: { field: string; value: unknown }[],
): LeadCentreLeadType | null {
  if (String(formID) === '5') return 'SELLER_APPRAISAL'
  const t = title.trim().toLowerCase()
  if (t === 'contact form') return 'OTHER'
  if (t === 'property alerts') return leadCentreTypeForPropertyAlerts(dataToSend)
  if (t === 'refer a client') return 'OTHER'
  if (t === 'careers expressions of interest') return 'OTHER'
  return null
}

/**
 * Refer A Client captures the REFERRER's details (your-first-name/your-email/
 * your-mobile) alongside the REFERRED person's (referral-first-name/
 * referral-last-name/referral-mobile — no referral-email field exists). The
 * actual sales lead is the referred person, so this form needs its own
 * extraction rather than the generic first-name/last-name/email/mobile slugs
 * extractContactFromFormData looks for.
 */
function extractLeadCentreContact(
  title: string,
  dataToSend: { field: string; value: unknown }[],
): { name: string; email?: string; phone?: string } {
  if (title.trim().toLowerCase() === 'refer a client') {
    const get = (key: string) => {
      const value = dataToSend.find((d) => d.field === key)?.value
      return value != null ? String(value).trim() : undefined
    }
    const name = [get('referral-first-name'), get('referral-last-name')].filter(Boolean).join(' ')
    return { name, phone: get('referral-mobile') }
  }
  return extractContactFromFormData(dataToSend)
}

export type FormBlockType = {
  blockName?: string
  blockType?: 'formBlock'
  /**
   * Set by call sites that already sit inside the canonical page container
   * (`ContentBlock` columns, `ContactPageLayout`). Rendered as a top-level block through
   * `RenderBlocks` the form has no padded ancestor, so it owns the gutter itself.
   */
  disableContainer?: boolean
  enableIntro: boolean
  usePlaceholders?: boolean
  form: FormType
  introContent?: DefaultTypedEditorState
  /** Careers EOI (Figma 4097:112398): "Attach your resume and CV." dropzone. */
  enableResumeUpload?: boolean
  /** Asana 018 — page-level form blocks pull the confirmation up so the gap above
   *  the tick matches the 32px below it; column-embedded forms (contact) must not. */
  confirmationPullUp?: boolean | null
  /** Server-resolved from `enableTestimonial` — see ServerComponent.tsx. */
  testimonial?: {
    quote: string
    authorName: string
    authorRole?: string | null
  } | null
}

export const FormBlock: React.FC<
  {
    id?: string
  } & FormBlockType
> = (props) => {
  const {
    disableContainer,
    enableIntro,
    usePlaceholders,
    form: formFromProps,
    form: { id: formID, confirmationMessage, confirmationType, redirect, submitButtonLabel } = {},
    introContent,
    testimonial,
    enableResumeUpload,
    confirmationPullUp,
  } = props

  const formMethods = useForm({
    defaultValues: formFromProps.fields,
  })
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = formMethods

  const [resumeFile, setResumeFile] = React.useState<File | null>(null)
  const resumeInputRef = React.useRef<HTMLInputElement>(null)

  const resumeDropzone = (
    /* Figma 4097:112398 — "Attach your resume and CV." muted dropzone */
    <div className="flex flex-col gap-6">
      <p className="text-body-md font-normal text-foreground">Attach your resume and CV.</p>
      <button
        type="button"
        onClick={() => resumeInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault()
          if (e.dataTransfer.files?.[0]) setResumeFile(e.dataTransfer.files[0])
        }}
        className="flex h-16 w-full flex-col items-center justify-center gap-1.5 rounded-sm bg-muted px-3 py-2 shadow-xs"
      >
        <svg
          className="size-6 text-muted-foreground"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
          <path d="M14 2v4a2 2 0 0 0 2 2h4" />
        </svg>
        <span className="text-body-sm font-light text-muted-foreground">
          {resumeFile ? resumeFile.name : 'Select or drag and drop files.'}
        </span>
      </button>
      <input
        ref={resumeInputRef}
        type="file"
        accept=".pdf,.doc,.docx"
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.[0]) setResumeFile(e.target.files[0])
          e.target.value = ''
        }}
      />
    </div>
  )

  // Single-field forms render the button inline with the field
  const inputFields = formFromProps?.fields?.filter((f) => f.blockType !== 'message') || []
  const isInlineForm = inputFields.length === 1

  // Figma Form 79:21583 — the form root is a flex column with `gap/gap-8` 32px. Its children
  // are Field Sets (79:21545, 2230:28002, 2229:26454, 2230:28358, 2229:11117, 2230:28123,
  // 2229:25732, 2230:28220, 2229:25968, 2230:27040, 79:21560) and, last, the submit button.
  // A Field Set is a heading (a `message` block) plus the fields that follow it; inside a set
  // the rhythm is `gap/gap-6` 24px vertical and `gap/gap-2` 8px horizontal.
  const fieldSets = useMemo(() => {
    const sets: FormFieldBlock[][] = []
    for (const field of formFromProps?.fields || []) {
      const currentSet = sets[sets.length - 1]
      if (!currentSet || field.blockType === 'message') sets.push([field])
      else currentSet.push(field)
    }
    return sets
  }, [formFromProps])

  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>()
  const [error, setError] = useState<{ message: string; status?: string } | undefined>()
  const router = useRouter()

  const onSubmit = useCallback(
    (data: FormFieldBlock[]) => {
      let loadingTimerID: ReturnType<typeof setTimeout>
      const submitForm = async () => {
        setError(undefined)

        const dataToSend = Object.entries(data).map(([name, value]) => ({
          field: name,
          value,
        }))

        // Resume/CV upload (Careers EOI) — store the file in the access-controlled
        // documents collection first, then record its admin link on the submission.
        if (enableResumeUpload && resumeFile) {
          try {
            const body = new FormData()
            body.append('file', resumeFile)
            const upload = await fetch(`${getClientSideURL()}/api/resume-uploads`, {
              method: 'POST',
              body,
            })
            if (upload.ok) {
              const doc = await upload.json()
              dataToSend.push({ field: 'resume', value: `${resumeFile.name} — ${doc.url}` } as any)
            } else {
              dataToSend.push({
                field: 'resume',
                value: `${resumeFile.name} (upload failed)`,
              } as any)
            }
          } catch {
            dataToSend.push({ field: 'resume', value: `${resumeFile.name} (upload failed)` } as any)
          }
        }

        // delay loading indicator by 1s
        loadingTimerID = setTimeout(() => {
          setIsLoading(true)
        }, 1000)

        try {
          const req = await fetch(`${getClientSideURL()}/api/form-submissions`, {
            body: JSON.stringify({
              form: formID,
              submissionData: dataToSend,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'POST',
          })

          const res = await req.json()

          clearTimeout(loadingTimerID)

          if (req.status >= 400) {
            setIsLoading(false)

            setError({
              message: res.errors?.[0]?.message || 'Internal Server Error',
              status: res.status,
            })

            return
          }

          setIsLoading(false)
          setHasSubmitted(true)

          const leadType = leadCentreTypeForForm(formID, formFromProps.title, dataToSend)
          if (leadType) {
            const { name, email, phone } = extractLeadCentreContact(formFromProps.title, dataToSend)
            reportToLeadCentre({
              name,
              email,
              phone,
              leadType,
              sourceForm: formFromProps.title || String(formID),
              fields: formDataToFields(dataToSend),
            })
          }

          // For the appraisal form, redirect with the address field value
          if (String(formID) === '5') {
            const addressEntry = dataToSend.find((d) => /address/i.test(d.field))
            if (addressEntry?.value) {
              router.push(
                `/appraisal/step-2?address=${encodeURIComponent(String(addressEntry.value))}`,
              )
              return
            }
          }

          if (confirmationType === 'redirect' && redirect) {
            const { url } = redirect

            const redirectUrl = url

            if (redirectUrl) router.push(redirectUrl)
          }
        } catch (err) {
          console.warn(err)
          setIsLoading(false)
          setError({
            message: 'Something went wrong.',
          })
        }
      }

      void submitForm()
    },
    [
      router,
      formID,
      redirect,
      confirmationType,
      resumeFile,
      enableResumeUpload,
      formFromProps.title,
    ],
  )

  const submitContent = isLoading ? (
    <>
      <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
      Sending...
    </>
  ) : (
    submitButtonLabel
  )

  // Figma Loading button 2198:34778 (Size=Regular / State=Default): the loading state swaps
  // the fill to theme/secondary #74cdde with a theme/foreground label and stays fully opaque.
  const submitLoadingClassName = isLoading
    ? 'bg-secondary text-secondary-foreground disabled:opacity-100'
    : undefined

  // Row gap is `gap/gap-2` 8px, so a field occupying `pct`% of the row gives up its share of
  // the row's gaps. At 100% nothing is subtracted — the field runs the full width of the
  // column (Figma 2230:28005 / 2230:28008 / 2230:28009).
  const fieldWidth = (rawWidth?: number): string => {
    const pct = typeof rawWidth === 'number' && rawWidth > 0 ? rawWidth : 100
    const gapShare = (8 * (100 - pct)) / 100
    return gapShare === 0 ? '100%' : `calc(${pct}% - ${gapShare}px)`
  }

  // Figma 4097:111068 uses `message` blocks in two typographic roles: a Field Set heading
  // (4097:112285 / 4097:112293 — Body/bodyMdAlt, weight 400) and the trailing standalone
  // disclaimer (4097:112299 — Body/bodyMd, weight 300). A set that carries fields after the
  // heading is the former; a set consisting of the message alone is the latter.
  const messageRoleFor = (
    field: FormFieldBlock,
    set: FormFieldBlock[],
  ): 'heading' | 'disclaimer' | undefined => {
    if (field.blockType !== 'message') return undefined
    return set.length > 1 ? 'heading' : 'disclaimer'
  }

  const renderField = (
    field: FormFieldBlock,
    key: React.Key,
    messageRole?: 'heading' | 'disclaimer',
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Field: React.FC<any> = fields?.[field.blockType as keyof typeof fields]
    if (!Field) return null

    return (
      <div
        className={cn(
          'min-w-0',
          isInlineForm && 'flex-1',
          // The Field Set heading carries its own `my-12` inside Message/index.tsx; within a
          // set the heading-to-field gap is the set's `gap/gap-6` 24px (Figma 2229:26454).
          field.blockType === 'message' && '[&>div]:my-0',
        )}
        key={key}
        style={
          isInlineForm ? undefined : { width: fieldWidth((field as { width?: number }).width) }
        }
      >
        <Field
          form={formFromProps}
          {...field}
          {...formMethods}
          control={control}
          errors={errors}
          messageRole={messageRole}
          register={register}
          usePlaceholders={usePlaceholders}
        />
      </div>
    )
  }

  return (
    // Figma 'CTA Block' 4097:111036 is 1600 wide; its content frame 4097:111037 sits at
    // x=64 w=1472 (`--spacing-container` on desktop) and the Form 4097:111068 starts at x=0
    // inside it — i.e. the form's left edge is the page gutter, not the viewport edge.
    // Nested call sites that already provide the gutter pass `disableContainer`.
    <div
      className={
        disableContainer
          ? undefined
          : 'mx-auto w-full max-w-[var(--container-content)] px-[var(--spacing-container)]'
      }
    >
      <div
        className={
          testimonial
            ? 'grid grid-cols-1 items-start gap-12 md:grid-cols-2 md:gap-8 lg:gap-16'
            : undefined
        }
      >
        <div className={isInlineForm ? undefined : 'max-w-[704px]'}>
          {enableIntro && introContent && !hasSubmitted && (
            <RichText
              // Figma form intros (EOI 4097:112398 etc.) are Body/bodyMd — 16 light
              className="mb-16 [&_p]:!text-body-md [&_p]:!font-light"
              data={introContent}
              enableGutter={false}
            />
          )}
          <div>
            <FormProvider {...formMethods}>
              {!isLoading && hasSubmitted && confirmationType === 'message' && (
                /* Asana 018 — every form success reads like the Appraisal thank-you:
                   shared tick mark (radius sm, 1px stroke) above the confirmation copy,
                   heading styled as a display heading via the prose overrides. */
                /* Asana 018 (Steve 2026-08-19): equalise the gaps around the tick —
                   the section padding above the form area leaves ~112px to the text
                   above; -mt-20 (80px) brings it to the same 32px as the gap below. */
                <div
                  className={`flex flex-col items-start gap-8 ${confirmationPullUp ? '-mt-20' : ''}`}
                >
                  <ThankYouMark />
                  <RichText
                    className="[&_h1]:font-display [&_h1]:text-display-3xl [&_h1]:font-normal [&_h2]:font-display [&_h2]:text-display-3xl [&_h2]:font-normal"
                    data={confirmationMessage}
                    enableGutter={false}
                  />
                </div>
              )}
              {/* Figma Alert Type=Error 2198:34211 — white surface, 1px theme/border, radius
                `xl` 12px, p-4, gap-2 with a 16px lucide `info` icon. The text stack
                (2198:34213) is a title + description pair, both theme/destructive, stacked
                with gap-2: title 2198:34214 Body/bodySmAlt (14/16 weight 400) and
                description 2198:34215 Body/bodySm (14/16 weight 300). */}
              {error && (
                <div
                  className="mb-6 flex items-start gap-2 rounded-xl border border-border bg-background p-4 text-destructive"
                  role="alert"
                >
                  <Info aria-hidden="true" className="size-4 shrink-0" />
                  <div className="flex min-w-0 flex-col gap-2">
                    <p className="text-body-sm font-normal">Something went wrong</p>
                    <p className="text-body-sm font-light">
                      {error.message || 'Please try again.'}
                    </p>
                  </div>
                </div>
              )}
              {!hasSubmitted && isInlineForm && (
                <form id={formID} onSubmit={handleSubmit(onSubmit)}>
                  {/* Same rhythm as the stacked branch — Figma Form 79:21583 gap/gap-6 24
                    vertical, gap/gap-2 8 horizontal. */}
                  <div className="flex flex-wrap items-end gap-x-2 gap-y-6">
                    {(formFromProps?.fields || []).map((field, index) => renderField(field, index))}
                    <Button
                      className={cn('shrink-0', submitLoadingClassName)}
                      disabled={isLoading}
                      form={formID}
                      type="submit"
                      variant="default"
                    >
                      {submitContent}
                    </Button>
                  </div>
                </form>
              )}
              {!hasSubmitted && !isInlineForm && (
                <form className="flex flex-col gap-8" id={formID} onSubmit={handleSubmit(onSubmit)}>
                  {fieldSets.map((set, setIndex) => (
                    <React.Fragment key={setIndex}>
                      {/* Figma 4097:112398 orders the resume dropzone BEFORE the final
                          "Anything else we should know about you?" message group. */}
                      {enableResumeUpload && setIndex === fieldSets.length - 1 && resumeDropzone}
                      <div className="flex flex-wrap gap-x-2 gap-y-6">
                        {set.map((field, index) =>
                          renderField(field, `${setIndex}-${index}`, messageRoleFor(field, set)),
                        )}
                      </div>
                    </React.Fragment>
                  ))}
                  <div className="flex justify-start">
                    <Button
                      className={submitLoadingClassName}
                      disabled={isLoading}
                      form={formID}
                      type="submit"
                      variant="default"
                    >
                      {submitContent}
                    </Button>
                  </div>
                </form>
              )}
            </FormProvider>
          </div>
        </div>

        {/* Figma "Frame 645/646" — testimonial sits in the RIGHT half of the right
          column, top-aligned with the form (4097:107709 / 4097:107827). */}
        {testimonial && (
          <div className="lg:grid lg:grid-cols-2 lg:gap-16">
            <div aria-hidden="true" className="hidden lg:block" />
            <blockquote data-visual="form-testimonial">
              <p className="text-body-sm font-normal text-foreground">
                &ldquo;{testimonial.quote}&rdquo;
              </p>
              <p className="mt-4 text-body-sm font-light text-foreground">
                {testimonial.authorName}
                {testimonial.authorRole ? `, ${testimonial.authorRole}` : ''}
              </p>
            </blockquote>
          </div>
        )}
      </div>
    </div>
  )
}
